import { z } from 'zod'

/**
 * Multi-tenancy mode of a partner on the shared deploy.
 *  - `partner-domain` — own Clerk satellite + host (runtime-skinned).
 *  - `reseller-only`  — tagged on Clerk orgs, lives on the main domain.
 */
export type PartnerMode = 'partner-domain' | 'reseller-only'

/**
 * Partner custom-domain triple. Mirrors the DB `partners.partner_domain`
 * jsonb column and the crm-app Clerk-satellite registry.
 *
 * Framework-agnostic (no `window`, no node APIs) so the same definition is
 * importable from both the Next.js backend and the crm-app SPA.
 */
export const PartnerDomainSchema = z.object({
	/** Root domain, no scheme / no subdomain (e.g. `payoren.com`). */
	rootDomain: z.string().min(2).max(255),
	/** Full host serving the CRM for this partner (e.g. `app.payoren.com`). */
	appHost: z.string().min(2).max(255),
	/** Clerk FAPI hostname (partner CNAMEs this to `frontend-api.clerk.services`). */
	clerkFapi: z.string().min(2).max(255),
	/**
	 * Optional Payment Dashboard host (e.g. `dashboard.kashupay.com`) on the
	 * shared `tpa-dashboard` Vercel project. Same Clerk satellite as `appHost`.
	 */
	dashboardHost: z.string().min(2).max(255).optional(),
})

export type PartnerDomain = z.infer<typeof PartnerDomainSchema>

/**
 * Source of a partner's visual identity:
 *  - `'code'` (default) — the `brand` key resolves to a committed brand file
 *    in the consuming app's brand registry (logo/color/copy shipped in-bundle).
 *  - `'db'` — no brand file; identity (logo/color/name) comes from the DB
 *    `partners.branding` overlay at runtime. Lets a non-dev onboard a partner
 *    end-to-end (the auto-published code entry carries no assets).
 */
export type PartnerBrandSource = 'code' | 'db'

/**
 * Canonical partner-mode config entry: a brand key + its domain triple.
 *
 * `TBrand` lets a consumer narrow `brand` to its own brand-key union (e.g.
 * the crm-app's `BrandName`) while this shared package stays
 * brand-registry-agnostic (`string`).
 *
 * `brand` is always present — it doubles as the partner's signup-attribution
 * key (the slug stamped on new accounts). For `brandSource: 'db'` entries it
 * need NOT map to a code brand file (the app falls back to the default brand +
 * DB branding overlay).
 */
export interface PartnerConfig<TBrand extends string = string> {
	/** Brand / attribution key. For `'code'` entries, also the brand-file key. */
	readonly brand: TBrand
	readonly rootDomain: string
	readonly appHost: string
	readonly clerkFapi: string
	/**
	 * Optional Payment Dashboard host on the same root (e.g. `dashboard.kashupay.com`).
	 * Included in CRM sign-in redirect allowlists when set.
	 */
	readonly dashboardHost?: string
	/** Defaults to `'code'` when omitted. */
	readonly brandSource?: PartnerBrandSource
}
